// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import { createStyles, keyframes } from "antd-style";
import React from "react";
import { useTranslation } from "react-i18next";

import { useAppTheme } from "../hooks/atoms/themes";

// The Backend.AI session/compute cube as 9 line edges. Each edge strokes on in
// `--i` order, holds, then erases — a spiral draw. Mirrors the loader in
// lablup/backend.ai-webui#7788 so the two products share a loading gesture.
// `--L` is each edge's length (14 for the axis-aligned edges, 13.81 ≈ the
// isometric diagonals — rounded from 13.806) and drives the stroke-dash draw.
// Keep this geometry in sync with the inline cube in index.html.
const EDGES = [
  { x1: 20, y1: 6, x2: 31.9, y2: 13, i: 2, L: 13.81 },
  { x1: 31.9, y1: 13, x2: 31.9, y2: 27, i: 4, L: 14 },
  { x1: 31.9, y1: 27, x2: 20, y2: 34, i: 5, L: 13.81 },
  { x1: 20, y1: 34, x2: 8.1, y2: 27, i: 7, L: 13.81 },
  { x1: 8.1, y1: 27, x2: 8.1, y2: 13, i: 8, L: 14 },
  { x1: 8.1, y1: 13, x2: 20, y2: 6, i: 1, L: 13.81 },
  { x1: 20, y1: 20, x2: 20, y2: 34, i: 6, L: 14 },
  { x1: 20, y1: 20, x2: 31.9, y2: 13, i: 3, L: 13.81 },
  { x1: 20, y1: 20, x2: 8.1, y2: 13, i: 0, L: 13.81 },
];

const draw = keyframes`
  0% { stroke-dashoffset: var(--L); }
  26% { stroke-dashoffset: 0; }
  72% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: calc(var(--L) * -1); }
`;

const useStyles = createStyles(({ css }, color: string) => ({
  edge: css`
    fill: none;
    stroke: ${color};
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: var(--L);
    stroke-dashoffset: var(--L);
    animation: ${draw} 1.8s ease-in-out infinite;
    animation-delay: calc(var(--i) * 0.1s);
    @media (prefers-reduced-motion: reduce) {
      animation: none;
      stroke-dashoffset: 0;
    }
  `,
}));

interface BAILoadingIndicatorProps {
  /** Width/height of the cube in px. */
  size?: number;
}

/**
 * The app's branded loading indicator: the Backend.AI wire-cube drawn in a
 * spiral, stroked in the deployment's primary color (`token.colorPrimary`), so
 * white-label installs get their brand color with no code change. Shared with
 * the boot splash (index.html) and used via FlexActivityIndicator.
 */
const BAILoadingIndicator: React.FC<BAILoadingIndicatorProps> = ({
  size = 24,
}) => {
  // useAppTheme reads the async theme.json atom, so on cold load this
  // component — even as a Suspense fallback — suspends until the atom
  // resolves (~one local fetch). That window is invisible by design: the boot
  // splash (index.html, z-index 10000) covers #root and is only dismissed
  // after App commits, which implies the atom has resolved. The trade-off
  // buys white-label colorPrimary without prop-drilling a color.
  const { token } = useAppTheme();
  const { t } = useTranslation("common");
  const { styles } = useStyles(token.colorPrimary);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      role="status"
      aria-label={t("Loading")}
    >
      {EDGES.map((e) => (
        <line
          key={e.i}
          className={styles.edge}
          x1={e.x1}
          y1={e.y1}
          x2={e.x2}
          y2={e.y2}
          style={{ "--i": e.i, "--L": e.L } as React.CSSProperties}
        />
      ))}
    </svg>
  );
};

export default BAILoadingIndicator;
